@if (asset(); as currentAsset) {
  <div class="asset-details" role="complementary" aria-label="Asset details">
    <!-- Header -->
    <div class="asset-details__header">
      <div class="asset-details__header-info">
        <img
          src="{{ currentAsset.type | assetIcon }}"
          alt="{{ currentAsset.name }}"
          width="24"
          height="24"
        />
        <div class="asset-details__header-text">
          <h3 class="asset-details__title">{{ currentAsset.name }}</h3>
          <!-- <span class="asset-details__type-badge">{{ currentAsset.type }}</span> -->
        </div>
      </div>
      <button
        tds-icon-button
        variant="tertiary"
        size="sm"
        label="Close details panel"
        aria-label="Close details panel"
        (click)="onClose()"
      >
        <tds-icon [name]="$any('close')"></tds-icon>
      </button>
    </div>

    <!-- Tabs -->
    <div class="asset-details__tabs">
      <tds-segmented-control [(selectedIndex)]="activeTabIndex">
        <button tds-segmented-control-option>Details</button>
        <button tds-segmented-control-option [disabled]="!hasTimeseries()">
          Timeseries
        </button>
      </tds-segmented-control>
    </div>

    <!-- Details Tab -->
    <div class="asset-details__content" [hidden]="activeTabIndex() !== 0">
      @if (currentAsset.metadata.description) {
        <section class="asset-details__section">
          <h4 class="asset-details__section-title">Description</h4>
          <p class="asset-details__description">
            {{ currentAsset.metadata.description }}
          </p>
        </section>
      }

      @if (currentAsset.metadata.owner || currentAsset.metadata.tags?.length) {
        <section class="asset-details__section">
          <h4 class="asset-details__section-title">General</h4>
          <dl class="asset-details__field-list">
            @if (currentAsset.metadata.owner) {
              <div class="asset-details__field">
                <dt class="asset-details__field-label">Owner</dt>
                <dd class="asset-details__field-value">
                  {{ currentAsset.metadata.owner }}
                </dd>
              </div>
            }
            @if (currentAsset.metadata.createdAt) {
              <div class="asset-details__field">
                <dt class="asset-details__field-label">Created</dt>
                <dd class="asset-details__field-value">
                  {{ currentAsset.metadata.createdAt | date: 'mediumDate' }}
                </dd>
              </div>
            }
            @if (currentAsset.metadata.updatedAt) {
              <div class="asset-details__field">
                <dt class="asset-details__field-label">Updated</dt>
                <dd class="asset-details__field-value">
                  {{ currentAsset.metadata.updatedAt | date: 'mediumDate' }}
                </dd>
              </div>
            }
            @if (currentAsset.metadata.tags?.length) {
              <div class="asset-details__field">
                <dt class="asset-details__field-label">Tags</dt>
                <dd class="asset-details__field-value">
                  <div class="asset-details__tags">
                    @for (tag of currentAsset.metadata.tags; track tag) {
                      <span class="asset-details__tag">{{ tag }}</span>
                    }
                  </div>
                </dd>
              </div>
            }
          </dl>
        </section>
      }

      @if (relevantProperties().length > 0) {
        <section class="asset-details__section">
          <h4 class="asset-details__section-title">General Properties</h4>
          <dl class="asset-details__field-list">
            @for (
              prop of relevantProperties();
              track prop.key;
              let i = $index
            ) {
              <div class="asset-details__field">
                <dt class="asset-details__field-label">{{ prop.label }}</dt>
                <dd class="asset-details__field-value">
                  @if (prop.isEditable && !prop.isObject) {
                    <lib-inline-edit-field
                      [value]="prop.value"
                      [propertyKey]="prop.key"
                      (saved)="onPropertySave($event, i)"
                    />
                  } @else {
                    {{ prop.value }}
                  }
                </dd>
              </div>
            }
          </dl>
        </section>
      }

      @if (jsonProperties().length > 0) {
        <section class="asset-details__section">
          <button
            class="asset-details__collapse-header"
            (click)="toggleStructuredProperties()"
            [attr.aria-expanded]="structuredPropertiesExpanded()"
          >
            <tds-icon
              [name]="
                structuredPropertiesExpanded()
                  ? 'keyboard_arrow_down'
                  : 'keyboard_arrow_right'
              "
              class="asset-details__collapse-icon"
            ></tds-icon>
            <h4 class="asset-details__section-title">Structured Properties</h4>
          </button>
          @if (structuredPropertiesExpanded()) {
            @for (prop of jsonProperties(); track prop.label) {
              <div class="asset-details__json-field">
                <dt class="asset-details__field-label">{{ prop.label }}</dt>
                <pre class="asset-details__json-value">{{ prop.value }}</pre>
              </div>
            }
          }
        </section>
      }

      @if (hasTechnicalProperties()) {
        <section class="asset-details__section">
          <button
            class="asset-details__collapse-header"
            (click)="toggleTechnicalDetails()"
            [attr.aria-expanded]="technicalDetailsExpanded()"
          >
            <tds-icon
              [name]="
                technicalDetailsExpanded()
                  ? 'keyboard_arrow_down'
                  : 'keyboard_arrow_right'
              "
              class="asset-details__collapse-icon"
            ></tds-icon>
            <h4 class="asset-details__section-title">System Properties</h4>
          </button>
          @if (technicalDetailsExpanded()) {
            <dl
              class="asset-details__field-list asset-details__field-list--nested"
            >
              @for (prop of technicalProperties(); track prop.label) {
                <div class="asset-details__field">
                  <dt class="asset-details__field-label">{{ prop.label }}</dt>
                  @if (prop.isObject) {
                    <dd class="asset-details__field-value">
                      <pre class="asset-details__json-value">{{
                        prop.value
                      }}</pre>
                    </dd>
                  } @else {
                    <dd class="asset-details__field-value">{{ prop.value }}</dd>
                  }
                </div>
              }
            </dl>
          }
        </section>
      }

      @if (hasRelationships()) {
        <section class="asset-details__section">
          <h4 class="asset-details__section-title">Relationships</h4>
          @for (group of relationshipNodes(); track group.value) {
            <div class="asset-details__relation-group">
              <span class="asset-details__relation-label">{{
                group.label
              }}</span>
              <ul class="asset-details__children-list">
                @for (child of group.children; track child.value) {
                  <li>
                    <button
                      class="asset-details__relation-link"
                      (click)="onNavigate(child.value)"
                    >
                      {{ child.label }}
                    </button>
                  </li>
                }
              </ul>
            </div>
          }
        </section>
      }
    </div>

    <!-- Timeseries Tab -->
    <div class="asset-details__content" [hidden]="activeTabIndex() !== 1">
      <lib-timeseries-tab
        [asset]="asset()"
        [timeseriesProperties]="timeseriesProperties()"
      />
    </div>

    <!-- Footer -->
    <div class="asset-details__footer" [hidden]="activeTabIndex() !== 0">
      <button tds-button variant="secondary" size="md" (click)="onCopyJson()">
        Copy as JSON
      </button>
    </div>
  </div>
}
